import { Vector2 } from 'three';

import { polygonFromSvgPoints, roundedSlotProfile, slotFromSvgRect } from './slot-profiles.helpers';

/**
 * Vent opening profiles for the 3D panel cutouts.
 *
 * A vent is drawn in 2D from an inline SVG (`component-svg/mok-vents-*.svg.tsx`), not from its
 * bounding box — the visible holes are the white slots, not the whole rectangle. These builders
 * reproduce those slot layouts as vertical-plane profiles so the 3D hole matches the 2D artwork.
 *
 * Constants below mirror the SVG sources exactly; if a slot layout changes there, change it here.
 * The SVGs remain the source of truth for the 2D view.
 */

const SLAT_HEIGHT = 0.25;
const SLAT_RADIUS = 0.125;      // == SLAT_HEIGHT / 2 -> stadium ends, as drawn

/** mok-vents-side.svg.tsx: GROUP_WIDTH / GROUP_GAP / SLAT_Y / MIN_EDGE_MARGIN */
const SIDE_GROUP_WIDTH = 8;
const SIDE_GROUP_GAP = 3;
const SIDE_SLAT_Y = [1.125, 1.875, 2.625];
const SIDE_MIN_EDGE_MARGIN = 1.5;
const SIDE_DOUBLE_WIDTH = (SIDE_GROUP_WIDTH * 2) + SIDE_GROUP_GAP;

/** mok-vents-back.svg.tsx: three groups at 0-5, 8-18, 21-26; two rows */
const BACK_GROUPS: Array<[number, number]> = [[0, 5], [8, 10], [21, 5]];
const BACK_SLAT_Y = [1.125, 1.875];

/** passthrough-vent.svg.tsx */
const PASSTHROUGH_POINTS: Array<[number, number]> = [[0, 1], [6, 1], [6, 0], [10, 0], [10, 2], [0, 2]];

/** A bar-depth side panel is too narrow for two groups and gets one centred group (GitHub #451). */
const isSingleGroup = (panelWidth?: number): boolean =>
	typeof panelWidth === 'number' && panelWidth < SIDE_DOUBLE_WIDTH + (2 * SIDE_MIN_EDGE_MARGIN);

const mokSideProfiles = (width: number, height: number, panelWidth?: number): Array<Array<Vector2>> => {
	const single = isSingleGroup(panelWidth);
	const groupOffsets = single ? [0] : [0, SIDE_GROUP_WIDTH + SIDE_GROUP_GAP];
	const left = ((single ? SIDE_GROUP_WIDTH : SIDE_DOUBLE_WIDTH) / -2) + (width / 2);

	return groupOffsets.flatMap((groupX) => SIDE_SLAT_Y.map((slatY) =>
		slotFromSvgRect(left + groupX, slatY - 2 + (height / 2), SIDE_GROUP_WIDTH, SLAT_HEIGHT, width, height, SLAT_RADIUS)
	));
};

const mokBackProfiles = (width: number, height: number): Array<Array<Vector2>> =>
	BACK_GROUPS.flatMap(([groupX, groupW]) => BACK_SLAT_Y.map((slatY) =>
		slotFromSvgRect(groupX, slatY, groupW, SLAT_HEIGHT, width, height, SLAT_RADIUS)
	));

/**
 * Returns one closed profile per visible opening, in the panel's front plane, centred on the
 * component. An unknown `svgKey` falls back to the component's own rectangle, which is what the
 * plain VENT-SIDE / VENT-BACK rows draw in 2D (they carry no svg).
 */
export const ventOpeningProfiles = (
	svgKey: string | null | undefined, width: number, height: number, panelWidth?: number
): Array<Array<Vector2>> => {
	if (width <= 0 || height <= 0) return [];

	switch (svgKey) {
		case 'mokVentsSide':
			return mokSideProfiles(width, height, panelWidth);
		case 'mokVentsBack':
			return mokBackProfiles(width, height);
		case 'passthroughVent':
			return [polygonFromSvgPoints(PASSTHROUGH_POINTS, width, height)];
		default:
			return [roundedSlotProfile({ cx: 0, cy: 0, width, height })];
	}
};
